import type { CustomFieldConfig, FieldType } from "@salestrak/custom-fields";

export const FORM_QUESTION_KINDS = [
  "shortText",
  "longText",
  "singleChoice",
  "multipleChoice",
  "yesNo",
  "number",
  "money",
  "date",
  "time",
  "email",
  "phone",
  "url",
  "address",
  "member",
] as const;
export type FormQuestionKind = (typeof FORM_QUESTION_KINDS)[number];

export const FORM_CONDITION_OPS = [
  "is",
  "isNot",
  "includes",
  "excludes",
  "isChecked",
  "isNotChecked",
  "isAnswered",
  "isBlank",
  "greaterThan",
  "lessThan",
  "between",
  "before",
  "after",
  "withinLastDays",
  "atLeast",
  "fewerThan",
] as const;
export type FormConditionOp = (typeof FORM_CONDITION_OPS)[number];

export type FormFieldOption = {
  label?: string | null;
  value?: string | null;
  color?: string | null;
};

export type FormFieldConfig = Omit<CustomFieldConfig, "options"> & {
  options?: ReadonlyArray<FormFieldOption | null> | null;
  defaultValue?: unknown;
  multiple?: boolean | null;
  includeTeams?: boolean | null;
  optionSort?: string | null;
  [key: string]: unknown;
};

export type FormField = {
  customFieldId: string;
  name: string;
  type: FieldType | (string & {});
  config?: FormFieldConfig | null;
};

/** A question shown only while this reads true. `whenItemId` always names a
 *  question earlier in the form, so a rule can never close a cycle. */
export type FormCondition = {
  whenItemId: string;
  op: FormConditionOp;
  /** Option values (never labels), or what a comparison compares against. */
  values?: string[];
};

export const FORM_PROPERTIES = [
  "name",
  "description",
  "issueType",
  "workflow",
  "stage",
  "assignees",
  "teams",
  "address",
  "issueSource",
] as const;
export type FormProperty = (typeof FORM_PROPERTIES)[number];

export const FORM_PROPERTY_FIELD_TYPE: Record<FormProperty, FieldType> = {
  address: "address",
  assignees: "member",
  description: "paragraph",
  issueSource: "select",
  issueType: "select",
  name: "text",
  stage: "select",
  teams: "labels",
  workflow: "select",
};

export const FORM_PROPERTY_LABEL: Record<FormProperty, string> = {
  address: "Address",
  assignees: "Assignees",
  description: "Description",
  issueSource: "Source",
  issueType: "Type",
  name: "Project name",
  stage: "Stage",
  teams: "Teams",
  workflow: "Workflow",
};

type FormItemBase = {
  itemId: string;
  label: string;
  description?: string | null;
  showWhen?: FormCondition;
};

type LinkedRules = {
  required: boolean;
  prefill?: boolean;
  requiredChoices?: string[];
  defaultChoices?: string[];
};

type AnyField = FormField;

export type FormItem<F extends AnyField = AnyField> =
  | (FormItemBase &
      LinkedRules & {
        kind: "customField";
        customFieldId: string;
        // Kept so a deleted field cannot retype answers already stored under it.
        fieldType?: FieldType;
      })
  | (FormItemBase & LinkedRules & { kind: "property"; property: FormProperty })
  | (FormItemBase & { kind: "question"; field: F; required: boolean; requiredChoices?: string[] })
  | (FormItemBase & { kind: "attachment"; field: F; required: boolean })
  // A section is a page break and never conditional; `parseFormShape` drops
  // a rule written on one.
  | (FormItemBase & { kind: "section" })
  | (FormItemBase & { kind: "text"; body: string });

export type FormItemKind = FormItem["kind"];

export type FormFieldItem<F extends AnyField = AnyField> = Extract<
  FormItem<F>,
  { kind: "customField" | "property" | "question" | "attachment" }
>;
export type FormLinkedItem<F extends AnyField = AnyField> = Extract<FormItem<F>, { kind: "customField" | "property" }>;
export type FormPhotoItem<F extends AnyField = AnyField> = Extract<FormItem<F>, { kind: "attachment" | "customField" }>;

export type FormStatus = "draft" | "live" | "closed";

export const FORM_VISIBILITIES = ["private", "public"] as const;
export type FormVisibility = (typeof FORM_VISIBILITIES)[number];

export type FormShare = { kind: "user" | "team"; id: string };

export type FormOffer = { workflowIds: string[]; issueTypeIds: string[] };

export const MAX_SUBMISSION_LIMIT = 10;
export const MAX_SUBMIT_LABEL_LENGTH = 40;

export type FormSettings = {
  visibility?: FormVisibility;
  offeredOn?: FormOffer;
  submissionLimit?: number;
  submitLabel?: string;
  resubmit?: boolean;
};

export type FormShape<F extends AnyField = AnyField> = {
  items: FormItem<F>[];
};

export type Form<F extends AnyField = AnyField> = FormShape<F> & {
  settings?: FormSettings | null;
  formId: string;
  name: string;
  description?: string | null;
  bannerUrl?: string | null;
  requiredByDefault?: boolean;
  sharedWith?: FormShare[];
  status: FormStatus;
  draft?: FormShape<F> | null;
  publishedVersionId?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
};

export type FormSubmissionStatus = "inProgress" | "submitted";

export type FormSubmission = {
  submissionId: string;
  formId: string;
  // Null only on a filling not stored yet, which the server pins to the current version.
  formVersionId: string | null;
  issueId: string;
  status: FormSubmissionStatus;
  answers: Record<string, unknown>;
  // An answer still equal to it is the Project's, not the filler's.
  prefilled?: Record<string, unknown> | null;
  itemIds?: string[] | null;
  deletedPhotoIds?: string[] | null;
  sharedWith?: FormShare[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy?: string | null;
  submittedAt?: string | null;
  submittedBy?: string | null;
};
